import React, { FC } from "react";
import i18next from "i18next";
import { useSelector } from "react-redux";
import classnames from "classnames";

import Table, { TableBody, TableHeader } from "../../../../components/Table";
import { TableProps } from "./types";
import { IconName } from "../../../../components/Icon/types";
import Icon from "../../../../components/Icon";
import { selectLicense } from "../../../../store/manageUsers";
import { useAbility } from "../../../../hooks";
import { ACTION, SUBJECT } from "../../../../constants/permissions";
import { OWNER } from "../../../../services/api/user/constants";
import {
  ITableColumnConfig,
  ITableDataParams,
} from "../../../../components/Table/types";
import { ICustomerPrincipal } from "../../../../services/api/user/types";

import "./styles.scss";

export const ManageUsersTable: FC<TableProps> = ({
  data,
  defaultSort,
  onEdit,
  onSort,
}) => {
  const licensePrincipals = useSelector(selectLicense)?.licenses || [];
  const ability = useAbility();
  const columns: ITableColumnConfig[] = [
    {
      field: "name",
      headerName: i18next.t("name"),
      cellClass: "ams-manage-users-table__cell",
      valueGetter: ({
        rowData,
      }: ITableDataParams<ICustomerPrincipal>): string => {
        return rowData?.name || "";
      },
    },
    {
      field: "email",
      headerName: i18next.t("email"),
      cellClass: "ams-manage-users-table__cell",
      valueGetter: ({
        rowData,
      }: ITableDataParams<ICustomerPrincipal>): string => {
        return rowData?.email || "";
      },
    },
    {
      field: "customer_role",
      headerName: i18next.t("role"),
      cellClass: "ams-manage-users-table__cell",
      valueGetter: ({
        rowData,
      }: ITableDataParams<ICustomerPrincipal>): string => {
        if (!rowData) {
          return "";
        }
        return i18next.t(rowData.customer_role.toLocaleLowerCase());
      },
    },
    {
      field: "productLicense",
      sortable: false,
      headerName: i18next.t("productLicense"),
      cellClass: "ams-manage-users-table__cell",
      cellRenderer: ({ rowData }: ITableDataParams<ICustomerPrincipal>) => {
        if (!rowData) {
          return <div />;
        }
        /* eslint-disable @typescript-eslint/camelcase */
        const haveLicense = licensePrincipals.some(({ principal_id }) => {
          return principal_id === rowData.id;
        });
        /* eslint-enable @typescript-eslint/camelcase */
        const icon: IconName = "active";

        return (
          <div className="ams-manage-users-table__license">
            <Icon
              className={classnames("ams-icon-status", {
                "ams-icon-status__active": haveLicense,
              })}
            >
              {icon}
            </Icon>
            <span className="pl-2xs">
              {haveLicense ? i18next.t("yes") : i18next.t("no")}
            </span>
          </div>
        );
      },
    },
    {
      field: "edit",
      headerName: i18next.t("editUser"),
      sortable: false,
      cellClass: "ams-manage-users-table__cell",
      cellRenderer: ({ rowData }: ITableDataParams<ICustomerPrincipal>) => {
        if (!rowData) {
          return <div />;
        }
        const icon: IconName = "edit";
        const isCanEdit =
          (rowData.customer_role !== OWNER &&
            ability.can(ACTION.READ, SUBJECT.TECHNICAL)) ||
          ability.can(ACTION.WRITE, SUBJECT.OWNER);

        return isCanEdit ? (
          <div
            onClick={() => {
              /* eslint-disable @typescript-eslint/camelcase */
              const haveLicense = licensePrincipals.some(({ principal_id }) => {
                return principal_id === rowData.id;
              });
              /* eslint-enable @typescript-eslint/camelcase */
              onEdit(rowData, haveLicense);
            }}
          >
            <Icon>{icon}</Icon>
          </div>
        ) : (
          "--"
        );
      },
    },
  ];

  return (
    <Table className="ams-manage-users-table">
      <TableHeader
        columns={columns}
        defaultSort={defaultSort}
        onSort={onSort}
      />
      <TableBody columns={columns} data={data} />
    </Table>
  );
};
